import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import max from 'lodash/max';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import { TooltipComponent, GridComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { renderToStaticMarkup } from 'react-dom/server';
import { useIsReducedMotion } from '@atlaskit/motion';
import { colors } from '@atlaskit/theme';
import { getTokenValue, token, useThemeObserver } from '@atlaskit/tokens';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl';
import { FireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	formatEstimateForTooltip,
	type EstimationStatistic,
} from '@atlassian/jira-software-report-charting-common/src/common/utils/format-estimate/index.tsx';
import { roundTo2DecimalPlaces } from '@atlassian/jira-software-report-charting-common/src/common/utils/round-to-2-decimals/index.tsx';
import ChartLegendItem from '@atlassian/jira-software-report-charting-common/src/ui/chart-legend/chart-legend-item/index.tsx';
import ChartLegend from '@atlassian/jira-software-report-charting-common/src/ui/chart-legend/index.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import type { BarChartItem } from '../../model/types';
import type { MessageType } from '../../state/selectors/chart';
import messages from './messages';

echarts.use([BarChart, TooltipComponent, GridComponent, CanvasRenderer]);

export interface Props {
	estimatedData: BarChartItem[];
	completedData: BarChartItem[];
	axisLabels: string[];
	yAxisLabelMessage: MessageType;
	estimationStatistic: EstimationStatistic;
	timeTrackingOptions: TimeTrackingOptions;
}

export type PropsWithIntl = Props & {
	intl: IntlShape;
};

type TooltipOptions = {
	estimationStatistic: EstimationStatistic;
	timeTrackingOptions: TimeTrackingOptions;
	intl: IntlShape;
};

type Point = {
	axisValue: string;
	value: number;
};

type Points = [Point | null, Point | null];

/**
 * Creates a tooltip renderer for ECharts.
 */
export const makeRenderTooltip =
	({ estimationStatistic, timeTrackingOptions, intl }: TooltipOptions) =>
	(points: Points): string => {
		// Explicitly put each point in a const so flow can correctly compute the type
		// (otherwise flow doesn't understand the null check)
		const point0 = points[0];
		const point1 = points[1];
		if (!point0 || !point1) {
			return '';
		}

		const commitment = point0.value;
		const completed = point1.value;
		const formattedCommitment = formatEstimateForTooltip(
			commitment,
			timeTrackingOptions,
			estimationStatistic,
			intl,
		);
		const formattedCompleted = formatEstimateForTooltip(
			completed,
			timeTrackingOptions,
			estimationStatistic,
			intl,
		);

		// Styled components are not a good idea here as there'll be no guarantee their CSS is on the
		// DOM.
		return renderToStaticMarkup(
			<div
				// eslint-disable-next-line jira/react/no-style-attribute
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					padding: token('space.100', '8px'),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					borderLeft: `8px solid ${token('color.background.input', colors.N100)}`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					borderRadius: 3,
				}}
			>
				<b>{point0.axisValue}</b>
				<br />
				Commitment: {formattedCommitment}
				<br />
				Completed: {formattedCompleted}
			</div>,
		);
	};

export const Chart = (props: PropsWithIntl) => {
	const {
		estimatedData,
		completedData,
		axisLabels,
		yAxisLabelMessage,
		estimationStatistic,
		timeTrackingOptions,
		intl,
		intl: { formatMessage },
	} = props;

	useThemeObserver();
	const maximumValue = max([
		...estimatedData.map(({ y }) => y),
		...completedData.map(({ y }) => y),
	]);

	const renderTooltip = useMemo(
		() => makeRenderTooltip({ estimationStatistic, timeTrackingOptions, intl }),
		[estimationStatistic, timeTrackingOptions, intl],
	);

	const prefersReducedMotion = useIsReducedMotion();
	const containerTestId = 'software-velocity-report.chart.chart-container';

	return (
		<Container data-testid={containerTestId}>
			<ChartLegend>
				<ChartLegendItem
					name={formatMessage(messages.commitment)}
					description={formatMessage(messages.commitmentDescription)}
					color={getTokenValue('color.chart.neutral', colors.N40)}
				/>
				<ChartLegendItem
					name={formatMessage(messages.completed)}
					description={formatMessage(messages.completedDescription)}
					color={getTokenValue('color.chart.success', colors.G300)}
				/>
			</ChartLegend>
			<ReactEchartsCore
				echarts={echarts}
				option={{
					animation: !prefersReducedMotion,
					xAxis: {
						type: 'category',
						data: axisLabels,
						splitLine: {
							show: false,
						},
						axisLine: {
							lineStyle: {
								color: getTokenValue('color.chart.neutral', '#c0c0c1'),
							},
						},
						axisLabel: {
							color: getTokenValue('color.chart.gray.boldest', '#545454'),
						},
					},
					yAxis: {
						splitLine: {
							show: false,
						},
						axisLine: {
							lineStyle: {
								color: getTokenValue('color.chart.neutral', '#c0c0c1'),
							},
						},
						axisLabel: {
							color: getTokenValue('color.chart.gray.boldest', '#545454'),
							showMaxLabel: false,
						},
						axisTick: {
							show: true,
						},
						nameLocation: 'center',
						// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
						max: maximumValue * 1.15,
						nameGap: 56,
						nameTextStyle: {
							color: getTokenValue('color.chart.gray.boldest', '#707070'),
							font: token('font.body'),
							fontWeight: getTokenValue('font.weight.medium'),
						},
						name: formatMessage(yAxisLabelMessage),
						type: 'value',
					},
					grid: {
						show: true,
						borderColor: getTokenValue('color.chart.neutral', '#c0c0c1'),
						top: 28,
						left: 72,
						right: 40,
						bottom: 96,
					},
					tooltip: {
						show: true,
						backgroundColor: getTokenValue('elevation.surface.overlay', 'white'),
						trigger: 'axis',
						padding: 0,
						transitionDuration: 0,
						textStyle: {
							color: getTokenValue('color.text.subtle', '#42526e'),
						},
						formatter: renderTooltip,
						renderMode: 'html',
						// TODO: https://product-fabric.atlassian.net/browse/DSP-3792
						extraCssText: 'box-shadow: 0 0 8px rgba(0, 0, 0, 0.3)',
						axisPointer: {
							type: 'shadow',
							shadowStyle: {
								color: getTokenValue('elevation.surface.raised.hovered', 'colors.N10A'),
								opacity: 0.4,
							},
						},
					},
					series: [
						{
							type: 'bar',
							cursor: 'initial',
							barGap: '0%',
							barMaxWidth: 50,
							name: 'Commitment',
							label: 'Commitment',
							itemStyle: {
								emphasis: {
									color: getTokenValue('color.chart.neutral', colors.N40),
								},
								color: getTokenValue('color.chart.neutral', colors.N40),
							},
							data: estimatedData.map(({ y }) => ({
								value: y,
							})),
						},
						{
							type: 'bar',
							cursor: 'initial',
							barGap: '0%',
							barMaxWidth: 50,
							name: 'Completed',
							label: 'Completed',
							itemStyle: {
								emphasis: {
									color: getTokenValue('color.chart.success', colors.G300),
								},
								color: getTokenValue('color.chart.success', colors.G300),
							},
							data: completedData.map(({ y }) => ({
								value: roundTo2DecimalPlaces(y),
							})),
						},
					],
				}}
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ width: '100%', height: 500 }}
			/>
			<FireTrackAnalytics eventName="velocityChart loaded" />
		</Container>
	);
};

export default injectIntl(Chart);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	height: '500px',
});
